'use client'

import ArticleContent from '@/app/components/blog/ArticleContent'
import Title from '@/app/components/common/Title'
import { PortableTextBlock } from '@sanity/types'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

export interface FAQItem {
	question: string
	answer: PortableTextBlock[] | string
}

interface FAQSectionProps {
	items: FAQItem[]
}

export default function FAQSection({ items }: FAQSectionProps) {
	const t = useTranslations('Blog')
	const [openIndex, setOpenIndex] = useState<number | null>(0)

	const sanitizeHtml = (html: string): string => {
		if (typeof window === 'undefined') {
			return html
		}

		try {
			const DOMPurify = require('isomorphic-dompurify')
			return DOMPurify.sanitize(html, {
				ALLOWED_TAGS: ['a', 'b', 'strong', 'em', 'ul', 'ol', 'li', 'br'],
				ALLOWED_ATTR: ['href'],
				FORBID_TAGS: ['style', 'script'],
			})
		} catch (error) {
			console.error('DOMPurify sanitization failed:', error)
			return html
		}
	}

	const toggleItem = (index: number) => {
		setOpenIndex(openIndex === index ? null : index)
	}

	return (
		<div className="article-faq">
			<div className="article-faq__container">
				<Title level={2} bold color="text-900" className="article-faq__title" testId="article-faq-title">
					{t('faqSectionTitleFull')}
				</Title>
				<div className="article-faq__list">
					{items.map((item, index) => {
						const isOpen = openIndex === index
						return (
							<div
								key={index}
								className={`article-faq__item ${isOpen ? 'article-faq__item--open' : ''}`}
							>
								<button
									type="button"
									className="article-faq__header"
									onClick={() => toggleItem(index)}
									aria-expanded={isOpen}
									aria-controls={`faq-answer-${index}`}
								>
									<Title level={3} bold color="text-950" className="article-faq__question" testId={`faq-question-${index}`}>
										{item.question}
									</Title>
									<span
										className={`article-faq__icon ${isOpen ? 'article-faq__icon--open' : ''}`}
										aria-hidden="true"
									>
										<svg
											width="24"
											height="24"
											viewBox="0 0 24 24"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M12 5V19"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
												className="article-faq__icon-vertical"
											/>
											<path
												d="M5 12H19"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</span>
								</button>
								<div
									id={`faq-answer-${index}`}
									className="article-faq__content"
									aria-hidden={!isOpen}
								>
									{typeof item.answer === 'string' ? (
										<div
											className="article-faq__answer"
											dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.answer) }}
										/>
									) : (
										<div className="article-faq__answer">
											<ArticleContent content={item.answer} />
										</div>
									)}
								</div>
							</div>
						)
					})}
				</div>
			</div>
		</div>
	)
}

